import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import { RefreshProvider } from "./lib/refresh";
import { I18nProvider } from "./lib/i18n";
import { UserGuard, AdminGuard } from "./components/Guards";

// 🚀 Eagerly load only what auth screens need. Dashboard also lazy so users on
//    the login screen don't download 1200 lines of Dashboard code they don't
//    yet need. Trimmed main-bundle from ~490KB down to ~250KB.
import AuthPage from "./pages/AuthPage";

// 💤 Lazy — user pages (loaded when route hits)
const Dashboard            = lazy(() => import("./pages/Dashboard"));
const ForgotPassword       = lazy(() => import("./pages/ForgotPassword"));
const AdminLogin           = lazy(() => import("./pages/AdminLogin"));
const Games                = lazy(() => import("./pages/Games"));
const ProfilePage          = lazy(() => import("./pages/ProfilePage"));
const MarketDetail         = lazy(() => import("./pages/MarketDetail"));
const JodiBetPage          = lazy(() => import("./pages/games/JodiBetPage"));
const HarufPage            = lazy(() => import("./pages/games/HarufPage"));
const CrossBetPage         = lazy(() => import("./pages/games/CrossBetPage"));
const ReferEarn            = lazy(() => import("./pages/ReferEarn"));
const InstantGamePage      = lazy(() => import("./pages/InstantGamePage"));
const DragonTigerGamePage  = lazy(() => import("./pages/DragonTigerGamePage"));
const CoinFlipGamePage     = lazy(() => import("./pages/CoinFlipGamePage"));
const AviatorGamePage      = lazy(() => import("./pages/AviatorGamePage"));
const MyBids               = lazy(() => import("./pages/MyBids"));
const Passbook             = lazy(() => import("./pages/Passbook"));
const Funds                = lazy(() => import("./pages/Funds"));
const DepositPage          = lazy(() => import("./pages/DepositPage"));
const WithdrawPage         = lazy(() => import("./pages/WithdrawPage"));
const Notifications        = lazy(() => import("./pages/Notifications"));
const MPinPage             = lazy(() => import("./pages/MPinPage"));
const Chat                 = lazy(() => import("./pages/Chat"));
const DownloadPage         = lazy(() => import("./pages/DownloadPage"));
const VideoGuide           = lazy(() => import("./pages/VideoGuide"));
// StaticPages has multiple named exports — wrap each for lazy loading
const staticPages = () => import("./pages/StaticPages");
const SupportPage   = lazy(() => staticPages().then(m => ({ default: m.SupportPage })));
const NoticePage    = lazy(() => staticPages().then(m => ({ default: m.NoticePage })));
const RatesPage     = lazy(() => staticPages().then(m => ({ default: m.RatesPage })));
const ChartsPage    = lazy(() => staticPages().then(m => ({ default: m.ChartsPage })));
const TutorialsPage = lazy(() => staticPages().then(m => ({ default: m.TutorialsPage })));
const IdeaPage      = lazy(() => staticPages().then(m => ({ default: m.IdeaPage })));
const SettingsPage  = lazy(() => staticPages().then(m => ({ default: m.SettingsPage })));

// 💤 Lazy — admin pages (huge — normal users never load these)
const AdminDashboard    = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminMarkets      = lazy(() => import("./pages/admin/AdminMarkets"));
const AdminResults      = lazy(() => import("./pages/admin/AdminResults"));
const AdminJantri       = lazy(() => import("./pages/admin/AdminJantri"));
const AdminUsers        = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPayments     = lazy(() => import("./pages/admin/AdminPayments"));
const AdminBids         = lazy(() => import("./pages/admin/AdminBids"));
const AdminSettings     = lazy(() => import("./pages/admin/AdminSettings"));
const AdminUserDetail   = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminWinners      = lazy(() => import("./pages/admin/AdminWinners"));
const AdminChat         = lazy(() => import("./pages/admin/AdminChat"));
const AdminFakeTickers  = lazy(() => import("./pages/admin/AdminFakeTickers"));
const AdminVideos       = lazy(() => import("./pages/admin/AdminVideos"));

// Named-export unwrappers handled above via lazy wrappers

function RouteLoader() {
  const [showRetry, setShowRetry] = React.useState(false);
  React.useEffect(() => {
    // If suspense doesn't resolve in 8s, something is likely stuck (chunk 404 after deploy).
    const t = setTimeout(() => setShowRetry(true), 8000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#04061a] px-6 text-center">
      <div className="w-14 h-14 rounded-full border-4 border-yellow-400/25 border-t-yellow-400 animate-spin" />
      <div className="mt-6 text-yellow-300 text-sm font-semibold tracking-widest uppercase">Loading J&K Club…</div>
      {showRetry && (
        <div className="mt-8 max-w-xs">
          <div className="text-white/80 text-sm mb-3">Zyada time lag raha hai. Refresh karke try karo:</div>
          <button
            onClick={() => window.location.reload()}
            data-testid="route-loader-retry"
            className="w-full h-11 rounded-lg bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-black tracking-wider shadow-lg active:opacity-90"
          >
            🔄 Refresh
          </button>
        </div>
      )}
    </div>
  );
}

// 🛡️ ChunkErrorBoundary: catches errors thrown by React.lazy() when a
// stale client tries to load a JS chunk that no longer exists on the
// server (very common after a fresh deploy). Instead of the blue
// forever-loading screen, we auto-reload the page ONCE so the client
// picks up the new build. Users won't get logged out (localStorage stays).
class ChunkErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { errored: false }; }
  static getDerivedStateFromError() { return { errored: true }; }
  componentDidCatch(error) {
    const msg = String(error?.message || error);
    const isChunkError =
      msg.includes("Loading chunk") ||
      msg.includes("ChunkLoadError") ||
      msg.includes("Failed to fetch dynamically imported module") ||
      /Loading CSS chunk/i.test(msg);
    if (isChunkError && !sessionStorage.getItem("__chunk_reloaded")) {
      sessionStorage.setItem("__chunk_reloaded", "1");
      window.location.reload();
    }
  }
  render() {
    if (this.state.errored) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#04061a] px-6 text-center">
          <div className="text-yellow-400 text-4xl mb-3">⚠️</div>
          <div className="text-white text-base font-semibold mb-2">App load nahi ho paya</div>
          <div className="text-white/60 text-sm mb-6">Please refresh — naya version aa gaya hai</div>
          <button
            onClick={() => { sessionStorage.removeItem("__chunk_reloaded"); window.location.reload(); }}
            className="h-11 px-8 rounded-lg bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-black tracking-wider shadow-lg"
            data-testid="chunk-error-retry"
          >
            🔄 Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <I18nProvider>
        <AuthProvider>
          <RefreshProvider>
          <ChunkErrorBoundary>
          <Suspense fallback={<RouteLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<AuthPage initialMode="login" />} />
            <Route path="/register" element={<AuthPage initialMode="register" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/login" element={<AdminLogin />} />

            {/* User app */}
            <Route path="/" element={<UserGuard><Dashboard /></UserGuard>} />
            <Route path="/market/:id" element={<UserGuard><MarketDetail /></UserGuard>} />
            <Route path="/market/:id/game/jodi" element={<UserGuard><JodiBetPage /></UserGuard>} />
            <Route path="/market/:id/game/haruf" element={<UserGuard><HarufPage /></UserGuard>} />
            <Route path="/market/:id/game/cross_bet" element={<UserGuard><CrossBetPage /></UserGuard>} />
            <Route path="/my-bids" element={<UserGuard><MyBids /></UserGuard>} />
            <Route path="/passbook" element={<UserGuard><Passbook /></UserGuard>} />
            <Route path="/funds" element={<UserGuard><Funds /></UserGuard>} />
            <Route path="/deposit" element={<UserGuard><DepositPage /></UserGuard>} />
            <Route path="/withdraw" element={<UserGuard><WithdrawPage /></UserGuard>} />
            <Route path="/notifications" element={<UserGuard><Notifications /></UserGuard>} />
            <Route path="/mpin" element={<UserGuard><MPinPage /></UserGuard>} />
            <Route path="/support" element={<UserGuard><SupportPage /></UserGuard>} />
            <Route path="/notice" element={<UserGuard><NoticePage /></UserGuard>} />
            <Route path="/rates" element={<UserGuard><RatesPage /></UserGuard>} />
            <Route path="/charts" element={<UserGuard><ChartsPage /></UserGuard>} />
            <Route path="/tutorials" element={<UserGuard><TutorialsPage /></UserGuard>} />
            <Route path="/idea" element={<UserGuard><IdeaPage /></UserGuard>} />
            <Route path="/settings" element={<UserGuard><SettingsPage /></UserGuard>} />
            <Route path="/share" element={<UserGuard><ReferEarn /></UserGuard>} />
            <Route path="/refer" element={<UserGuard><ReferEarn /></UserGuard>} />
            <Route path="/games" element={<UserGuard><Games /></UserGuard>} />
            <Route path="/profile" element={<UserGuard><ProfilePage /></UserGuard>} />
            <Route path="/games/dragon-tiger" element={<UserGuard><DragonTigerGamePage /></UserGuard>} />
            <Route path="/games/coin-flip" element={<UserGuard><CoinFlipGamePage /></UserGuard>} />
            <Route path="/games/aviator" element={<UserGuard><AviatorGamePage /></UserGuard>} />
            <Route path="/chat" element={<UserGuard><Chat /></UserGuard>} />
            <Route path="/video-guide" element={<UserGuard><VideoGuide /></UserGuard>} />
            <Route path="/download" element={<DownloadPage />} />

            {/* Admin */}
            <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
            <Route path="/admin/markets" element={<AdminGuard><AdminMarkets /></AdminGuard>} />
            <Route path="/admin/results" element={<AdminGuard><AdminResults /></AdminGuard>} />
            <Route path="/admin/jantri" element={<AdminGuard><AdminJantri /></AdminGuard>} />
            <Route path="/admin/users" element={<AdminGuard><AdminUsers /></AdminGuard>} />
            <Route path="/admin/users/:id" element={<AdminGuard><AdminUserDetail /></AdminGuard>} />
            <Route path="/admin/payments" element={<AdminGuard><AdminPayments /></AdminGuard>} />
            <Route path="/admin/bids" element={<AdminGuard><AdminBids /></AdminGuard>} />
            <Route path="/admin/settings" element={<AdminGuard><AdminSettings /></AdminGuard>} />
            <Route path="/admin/winners" element={<AdminGuard><AdminWinners /></AdminGuard>} />
            <Route path="/admin/chat" element={<AdminGuard><AdminChat /></AdminGuard>} />
            <Route path="/admin/fake-tickers" element={<AdminGuard><AdminFakeTickers /></AdminGuard>} />
            <Route path="/admin/videos" element={<AdminGuard><AdminVideos /></AdminGuard>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          </ChunkErrorBoundary>
          </RefreshProvider>
        </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
